import { Browser, Animation } from '@syncfusion/ej2-base';
/**
 * Highlight behavior module for Sankey Chart.
 */
export class SankeyHighlight {
    /**
     * Constructor.
     *
     * @param {Sankey} chart - Sankey chart instance.
     */
    constructor(chart) {
        this.lastHoveredId = null;
        this.animationTimeoutId = null;
        /** Default animation duration for highlight removal in milliseconds. */
        this.DEFAULT_ANIMATION_DURATION = 400;
        /** Minimum animation duration to ensure smooth transitions. */
        this.MIN_ANIMATION_DURATION = 50;
        /** Epsilon value for floating point opacity comparison. */
        this.OPACITY_EPSILON = 0.001;
        this.chart = chart;
        this.wireEvents();
    }
    /**
     * Wires legend interaction events to the chart for hover, move, click, and touch end behaviors.
     *
     * @returns {void}
     */
    wireEvents() {
        if (this.chart.isDestroyed) {
            return;
        }
        const cancelEvent = Browser.isPointer ? 'pointerleave' : 'mouseleave';
        this.chart.on(cancelEvent, this.handleMouseLeave, this);
        this.chart.on(Browser.touchMoveEvent, this.handleMouseMove, this);
        // clear highlight on touch end / pointer up (chart notifies this event)
        this.chart.on(Browser.touchEndEvent, this.handleMouseMove, this);
    }
    /**
     * Unwires legend interaction events from the chart to release handlers and avoid memory leaks.
     *
     * @returns {void}
     */
    unwireEvents() {
        if (this.chart.isDestroyed) {
            return;
        }
        this.chart.off(Browser.touchMoveEvent, this.handleMouseMove);
        this.chart.off(Browser.touchEndEvent, this.handleMouseMove);
        const cancelEvent = Browser.isPointer ? 'pointerleave' : 'mouseleave';
        this.chart.off(cancelEvent, this.handleMouseLeave);
    }
    /**
     * Retrieves all label elements from the chart's label collection.
     *
     * @returns {HTMLElement[]} - Array of label text elements, empty array if collection not found.
     * @private
     */
    getLabelElements() {
        const labelCollection = document.getElementById(this.chart.element.id + '_label_collection');
        return labelCollection ? Array.prototype.slice.call(labelCollection.querySelectorAll('text')) : [];
    }
    /**
     * Clears active highlight when the pointer leaves the chart surface.
     *
     * @param {Event} _event - The leave event (unused).
     * @returns {void}
     * @private
     */
    handleMouseLeave(_event) {
        this.clearHighlights();
    }
    /**
     * Tracks pointer/touch movement to identify interactive nodes/links and apply the corresponding highlight.
     * Uses debouncing to prevent duplicate highlight updates from multiple event sources.
     *
     * @param {PointerEvent | TouchEvent} event - The pointer or touch move event used to detect the hovered element.
     * @returns {void}
     * @private
     */
    handleMouseMove(event) {
        const targetElement = (event && event.target);
        if (!targetElement) {
            this.clearHighlights();
            return;
        }
        const hitElement = this.getInteractiveTarget(targetElement);
        if (!hitElement) {
            this.clearHighlights();
            return;
        }
        if (hitElement.type === 'node' && hitElement.id) {
            if (this.lastHoveredId === 'node:' + hitElement.id) {
                return;
            }
            if (this.animationTimeoutId !== null) {
                clearTimeout(this.animationTimeoutId);
                this.animationTimeoutId = null;
            }
            this.stopAllAnimations();
            this.lastHoveredId = 'node:' + hitElement.id;
            this.highlightForNode(hitElement.id);
        }
        else if (hitElement.type === 'link') {
            if (this.lastHoveredId === 'link:' + (hitElement.id)) {
                return;
            }
            if (this.animationTimeoutId !== null) {
                clearTimeout(this.animationTimeoutId);
                this.animationTimeoutId = null;
            }
            this.stopAllAnimations();
            this.lastHoveredId = 'link:' + (hitElement.id);
            this.highlightForLink(hitElement.source, hitElement.target);
        }
        else {
            this.clearHighlights(true);
        }
    }
    /**
     * Walks up the DOM tree to find the nearest interactive Sankey element (node, link, label, or legend item).
     *
     * @param {Element} element - The starting DOM element from the event target.
     * @returns {Element | null} - returns element if found, or null.
     * @private
     */
    getInteractiveTarget(element) {
        let currentElement = element;
        while (currentElement && currentElement !== document.body) {
            const elementId = currentElement.getAttribute('id');
            if (elementId) {
                if (elementId.indexOf('_node_level_') > -1) {
                    // node element
                    const nodeId = currentElement.getAttribute('aria-label');
                    return { type: 'node', id: nodeId };
                }
                else if (elementId.indexOf('_link_level_') > -1) {
                    const sourceId = currentElement.getAttribute('data-source');
                    const targetId = currentElement.getAttribute('data-target');
                    return { type: 'link', id: elementId, source: sourceId, target: targetId };
                }
                else if (elementId.indexOf('_label_level_') > -1) {
                    // labels map to node by level/index pattern
                    const splitIdParts = elementId.split('_');
                    const level = splitIdParts[splitIdParts.length - 2];
                    const index = splitIdParts[splitIdParts.length - 1];
                    const nodeElementId = `${this.chart.element.id}_node_level_${level}_${index}`;
                    const nodeElement = document.getElementById(nodeElementId);
                    if (nodeElement) {
                        const nodeLabel = nodeElement.getAttribute('aria-label');
                        return { type: 'node', id: nodeLabel };
                    }
                }
                else if (elementId.indexOf('_legend_') > -1 && this.chart.legendSettings.enableHighlight) {
                    let legendHighlightElement;
                    if (elementId.indexOf('_shape_') > -1 || elementId.indexOf('_text_') > -1) {
                        legendHighlightElement = currentElement.parentElement;
                    }
                    else {
                        legendHighlightElement = currentElement;
                    }
                    const nodeId = legendHighlightElement.getAttribute('aria-label');
                    return { type: 'node', id: nodeId };
                }
            }
            currentElement = currentElement.parentElement;
        }
        return null;
    }
    /**
     * Highlights the hovered node along with its directly connected neighbor nodes and links by applying active/inactive opacities.
     *
     * @param {string} nodeId - The node id whose related nodes and links should be highlighted.
     * @returns {void}
     * @private
     */
    highlightForNode(nodeId) {
        const chart = this.chart;
        const linkCollection = document.getElementById(chart.element.id + '_link_collection');
        const nodeCollection = document.getElementById(chart.element.id + '_node_collection');
        const labelCollection = document.getElementById(chart.element.id + '_label_collection');
        if (!linkCollection || !nodeCollection) {
            return;
        }
        const linkElements = Array.prototype.slice.call(linkCollection.querySelectorAll('path'));
        const nodeElements = Array.prototype.slice.call(nodeCollection.querySelectorAll('rect'));
        const labelElements = labelCollection ? Array.prototype.slice.call(labelCollection.querySelectorAll('text')) : [];
        // collect neighbor node ids connected to hovered node
        const neighborNodeMap = {};
        const highlightOpacity = (chart.linkStyle && chart.linkStyle.highlightOpacity);
        const inactiveOpacity = (chart.linkStyle && chart.linkStyle.inactiveOpacity);
        const nodeHighlightOpacity = (chart.nodeStyle && chart.nodeStyle.highlightOpacity);
        const nodeInactiveOpacity = (chart.nodeStyle && chart.nodeStyle.inactiveOpacity);
        for (const linkElement of linkElements) {
            const sourceId = linkElement.getAttribute('data-source');
            const targetId = linkElement.getAttribute('data-target');
            if (sourceId === nodeId || targetId === nodeId) {
                // matched link
                linkElement.setAttribute('opacity', String(highlightOpacity));
                // mark the neighbor node (other end)
                const otherNodeId = (sourceId === nodeId) ? targetId : sourceId;
                if (otherNodeId) {
                    neighborNodeMap[otherNodeId] = true;
                }
            }
            else {
                linkElement.setAttribute('opacity', String(inactiveOpacity));
            }
        }
        for (const nodeElement of nodeElements) {
            const nodeElementId = nodeElement.getAttribute('aria-label');
            if (nodeElementId === nodeId || (nodeElementId && neighborNodeMap[nodeElementId])) {
                nodeElement.setAttribute('opacity', String(nodeHighlightOpacity));
            }
            else {
                nodeElement.setAttribute('opacity', String(nodeInactiveOpacity));
            }
        }
        this.highlightLabelsForNodes([nodeId, ...Object.keys(neighborNodeMap)], labelElements, inactiveOpacity);
    }
    /**
     * Highlights the hovered link and its source/target nodes by applying active/inactive opacities.
     *
     * @param {string | null} source - The source node id of the hovered link.
     * @param {string | null} target - The target node id of the hovered link.
     * @returns {void}
     * @private
     */
    highlightForLink(source, target) {
        const chart = this.chart;
        const linkCollection = document.getElementById(chart.element.id + '_link_collection');
        const nodeCollection = document.getElementById(chart.element.id + '_node_collection');
        const labelCollection = document.getElementById(chart.element.id + '_label_collection');
        if (!linkCollection || !nodeCollection) {
            return;
        }
        const linkElements = Array.prototype.slice.call(linkCollection.querySelectorAll('path'));
        const nodeElements = Array.prototype.slice.call(nodeCollection.querySelectorAll('rect'));
        const labelElements = labelCollection ? Array.prototype.slice.call(labelCollection.querySelectorAll('text')) : [];
        const highlightOpacity = (chart.linkStyle && chart.linkStyle.highlightOpacity);
        const inactiveOpacity = (chart.linkStyle && chart.linkStyle.inactiveOpacity);
        const nodeHighlightOpacity = (chart.nodeStyle && chart.nodeStyle.highlightOpacity);
        const nodeInactiveOpacity = (chart.nodeStyle && chart.nodeStyle.inactiveOpacity);
        for (const linkElement of linkElements) {
            const sourceId = linkElement.getAttribute('data-source');
            const targetId = linkElement.getAttribute('data-target');
            if (sourceId === source && targetId === target) {
                linkElement.setAttribute('opacity', String(highlightOpacity));
            }
            else {
                linkElement.setAttribute('opacity', String(inactiveOpacity));
            }
        }
        for (const nodeElement of nodeElements) {
            const nodeElementId = nodeElement.getAttribute('aria-label');
            if (nodeElementId === source || nodeElementId === target) {
                nodeElement.setAttribute('opacity', String(nodeHighlightOpacity));
            }
            else {
                nodeElement.setAttribute('opacity', String(nodeInactiveOpacity));
            }
        }
        const highlightedNodeIds = [];
        if (source) {
            highlightedNodeIds.push(source);
        }
        if (target) {
            highlightedNodeIds.push(target);
        }
        this.highlightLabelsForNodes(highlightedNodeIds, labelElements, inactiveOpacity);
    }
    /**
     * Clears active node/link highlight and restores default link and node opacity values.
     * Uses smooth animation for opacity transitions when truly leaving the chart.
     * Set animate to false for immediate clearing (e.g., on chart destruction).
     *
     * @param {boolean} [animate=true] - Whether to use animation for the clear transition.
     *                                    True: smooth fade over DEFAULT_ANIMATION_DURATION ms.
     *                                    False: immediate opacity reset to defaults.
     * @returns {void}
     * @private
     */
    clearHighlights(animate = true) {
        if (this.lastHoveredId === null) {
            return;
        }
        this.lastHoveredId = null;
        if (animate) {
            this.performClearHighlightsWithAnimation();
        }
    }
    /**
     * Stops all ongoing animations on link, node, and label elements without changing their opacity.
     * Used when switching between highlights to allow new highlight values to be applied immediately.
     *
     * @returns {void}
     * @private
     */
    stopAllAnimations() {
        const chart = this.chart;
        const linkCollection = document.getElementById(chart.element.id + '_link_collection');
        const nodeCollection = document.getElementById(chart.element.id + '_node_collection');
        if (!linkCollection || !nodeCollection) {
            return;
        }
        const linkElements = Array.prototype.slice.call(linkCollection.querySelectorAll('path'));
        const nodeElements = Array.prototype.slice.call(nodeCollection.querySelectorAll('rect'));
        const labelElements = this.getLabelElements();
        for (const linkElement of linkElements) {
            if (linkElement.hasAttribute('e-animate')) {
                linkElement.removeAttribute('e-animate');
                Animation.stop(linkElement);
            }
        }
        for (const nodeElement of nodeElements) {
            if (nodeElement.hasAttribute('e-animate')) {
                nodeElement.removeAttribute('e-animate');
                Animation.stop(nodeElement);
            }
        }
        for (const labelElement of labelElements) {
            if (labelElement.hasAttribute('e-animate')) {
                labelElement.removeAttribute('e-animate');
                Animation.stop(labelElement);
            }
        }
    }
    /**
     * Performs the clearing of highlights with smooth animation for opacity values.
     * Animates all link, node, and label opacity changes to the default values.
     *
     * @returns {void}
     * @private
     */
    performClearHighlightsWithAnimation() {
        if (this.chart.isDestroyed) {
            return;
        }
        const chart = this.chart;
        const linkCollection = document.getElementById(chart.element.id + '_link_collection');
        const nodeCollection = document.getElementById(chart.element.id + '_node_collection');
        if (!linkCollection || !nodeCollection) {
            return;
        }
        const linkElements = Array.prototype.slice.call(linkCollection.querySelectorAll('path'));
        const nodeElements = Array.prototype.slice.call(nodeCollection.querySelectorAll('rect'));
        const labelElements = this.getLabelElements();
        const defaultLinkOpacity = (chart.linkStyle && chart.linkStyle.opacity);
        const defaultNodeOpacity = (chart.nodeStyle && chart.nodeStyle.opacity);
        for (const linkElement of linkElements) {
            const currentOpacity = parseFloat(linkElement.getAttribute('opacity'));
            if (currentOpacity && Math.abs(currentOpacity - defaultLinkOpacity) > this.OPACITY_EPSILON) {
                this.animateOpacityChange(linkElement, currentOpacity, defaultLinkOpacity, this.DEFAULT_ANIMATION_DURATION);
            }
        }
        for (const nodeElement of nodeElements) {
            const currentOpacity = parseFloat(nodeElement.getAttribute('opacity'));
            if (currentOpacity && Math.abs(currentOpacity - defaultNodeOpacity) > this.OPACITY_EPSILON) {
                this.animateOpacityChange(nodeElement, currentOpacity, defaultNodeOpacity, this.DEFAULT_ANIMATION_DURATION);
            }
        }
        for (const labelElement of labelElements) {
            const currentOpacity = parseFloat(labelElement.getAttribute('opacity') || '1');
            if (Math.abs(currentOpacity - 1) > this.OPACITY_EPSILON) {
                this.animateOpacityChange(labelElement, currentOpacity, 1, this.DEFAULT_ANIMATION_DURATION, true);
            }
        }
        if (this.DEFAULT_ANIMATION_DURATION > 0) {
            this.animationTimeoutId = window.setTimeout(() => {
                this.animationTimeoutId = null;
            }, Math.max(this.DEFAULT_ANIMATION_DURATION, this.MIN_ANIMATION_DURATION));
        }
    }
    /**
     * Animates opacity change from start value to end value for an element.
     * Includes proper cleanup on chart destruction.
     *
     * @param {HTMLElement} element - The element to animate.
     * @param {number} startOpacity - The starting opacity value.
     * @param {number} endOpacity - The ending opacity value.
     * @param {number} duration - The animation duration in milliseconds.
     * @param {boolean} removeAttribute - Whether to remove the opacity attribute at the end (for labels).
     * @returns {void}
     * @private
     */
    animateOpacityChange(element, startOpacity, endOpacity, duration, removeAttribute) {
        if (!element) {
            return;
        }
        const effectiveDuration = Math.max(duration, this.MIN_ANIMATION_DURATION);
        if (duration === 0) {
            if (removeAttribute) {
                element.removeAttribute('opacity');
            }
            else if (endOpacity < 1) {
                element.setAttribute('opacity', String(endOpacity));
            }
            else {
                element.removeAttribute('opacity');
            }
            return;
        }
        element.setAttribute('e-animate', 'true');
        new Animation({}).animate(element, {
            duration: effectiveDuration,
            progress: (args) => {
                // Stop animation if chart was destroyed
                if (this.chart.isDestroyed) {
                    Animation.stop(element);
                    return;
                }
                element.style.animation = '';
                const progress = Math.min(args.timeStamp / args.duration, 1);
                const currentOpacity = startOpacity + (endOpacity - startOpacity) * progress;
                element.setAttribute('opacity', String(parseFloat(currentOpacity.toFixed(3))));
            },
            end: () => {
                // Only apply final opacity if animation was not interrupted (e-animate marker exists)
                if (element.hasAttribute('e-animate')) {
                    if (removeAttribute) {
                        element.removeAttribute('opacity');
                    }
                    else if (endOpacity < 1) {
                        element.setAttribute('opacity', String(endOpacity));
                    }
                    else {
                        element.removeAttribute('opacity');
                    }
                    element.removeAttribute('e-animate');
                }
            }
        });
    }
    /**
     * Highlights labels for the specified nodes and dims all other labels.
     *
     * @param {string[]} highlightedNodeIds - Array of node ids whose labels should be highlighted.
     * @param {SVGElement[]} labelElements - Array of all label text elements in the chart.
     * @param {number} inactiveOpacity - The opacity value to apply to non-highlighted labels.
     * @returns {void}
     * @private
     */
    highlightLabelsForNodes(highlightedNodeIds, labelElements, inactiveOpacity) {
        for (const labelElement of labelElements) {
            const labelElementId = labelElement.getAttribute('id');
            if (!labelElementId) {
                continue;
            }
            const nodeLabel = this.getNodeLabelFromLabelElement(labelElement);
            if (nodeLabel && highlightedNodeIds.indexOf(nodeLabel) > -1) {
                labelElement.removeAttribute('opacity');
            }
            else {
                labelElement.setAttribute('opacity', String(inactiveOpacity));
            }
        }
    }
    /**
     * Extracts the node label/id from a label text element by finding the corresponding node.
     *
     * @param {SVGElement} labelElement - The label text element to extract the node id from.
     * @returns {string | null} returns the node id if found, else null.
     * @private
     */
    getNodeLabelFromLabelElement(labelElement) {
        const labelElementId = labelElement.getAttribute('id');
        if (!labelElementId) {
            return null;
        }
        const match = labelElementId.match(/_label_level_(\d+)_(\d+)$/);
        if (!match) {
            return null;
        }
        const level = match[1];
        const index = match[2];
        const chartId = this.chart.element.id;
        const nodeElementId = `${chartId}_node_level_${level}_${index}`;
        const nodeElement = document.getElementById(nodeElementId);
        if (nodeElement) {
            return nodeElement.getAttribute('aria-label');
        }
        return null;
    }
    /**
     * Gets the module name for the Sankey highlight component.
     *
     * @returns {string} returns module name
     * @private
     */
    getModuleName() { return 'SankeyHighlight'; }
    /**
     * Destroys the highlight module by unwiring events and cleaning up any pending animations.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        if (this.animationTimeoutId !== null) {
            clearTimeout(this.animationTimeoutId);
            this.animationTimeoutId = null;
        }
        this.unwireEvents();
    }
}
